import React, { createContext, useContext, useState, useEffect, useRef } from 'react'
import { API_BASE } from '../config'

const ProductsContext = createContext({ products: [], loading: true, error: null, refresh: () => {} })

const CACHE_KEY = 'revive_products_cache_v9'
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

function loadCache() {
  try {
    const c = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null')
    if (c && c.ts && Date.now() - c.ts < CACHE_TTL && Array.isArray(c.data) && c.data.length > 0) return c.data
  } catch {}
  return null
}

function saveCache(data) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data })) } catch {}
}

export function ProductsProvider({ children }) {
  const cached = loadCache()
  const [products, setProducts] = useState(cached || [])
  const [loading, setLoading] = useState(!cached)
  const [error, setError] = useState(null)
  const retryRef = useRef(null)

  const fetchProducts = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`${API_BASE}/api/products`, { cache: 'no-store' })
      if (!res.ok) throw new Error(`Server error ${res.status}`)
      const data = await res.json()
      const list = data.products
      if (list && list.length > 0) {
        setProducts(list)
        saveCache(list)
      } else {
        setError('API returned 0 products')
      }
    } catch(e) {
      setError(e.message)
      // retry after 5s
      if (retryRef.current) clearTimeout(retryRef.current)
      retryRef.current = setTimeout(fetchProducts, 5000)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchProducts()
    return () => { if (retryRef.current) clearTimeout(retryRef.current) }
  }, [])

  return (
    <ProductsContext.Provider value={{ products, loading, error, refresh: fetchProducts }}>
      {children}
    </ProductsContext.Provider>
  )
}

export function useProducts() {
  return useContext(ProductsContext)
}
